import { pgTable, pgEnum, text, timestamp, integer, uniqueIndex, index, foreignKey, jsonb, vector } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { user } from './user';

/**
 * Типы сущностей, которые могут быть векторизованы для рекомендаций
 */
export const embeddableEntityType = pgEnum('EmbeddableEntityType', [
  'COURSE',
  'ARTICLE',
  'ANNOUNCEMENT',
  'POST'
]);

/**
 * ContentEmbedding - хранение эмбеддингов для всех типов контента
 *
 * Полиморфная таблица с entityType + entityId для универсального хранения.
 * Использует pgvector для хранения и поиска по косинусному расстоянию.
 */
export const contentEmbedding = pgTable('ContentEmbedding', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Полиморфная связь
  entityType: embeddableEntityType().notNull(),
  entityId: integer().notNull(),

  // Текст, который был векторизован (для дебага и перегенерации)
  sourceText: text().notNull(),

  // Вектор (multilingual-e5-small = 384 dimensions)
  embedding: vector({ dimensions: 384 }).notNull(),

  // Версия модели (для инвалидации при смене модели)
  modelVersion: text().default('multilingual-e5-small-v1').notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  // Уникальный индекс: одна сущность - один эмбеддинг
  uniqueIndex('ContentEmbedding_entityType_entityId_key')
    .using('btree', table.entityType.asc(), table.entityId.asc()),

  // Индекс для поиска по типу
  index('ContentEmbedding_entityType_idx')
    .using('btree', table.entityType.asc()),

  // Индекс по модели для инвалидации
  index('ContentEmbedding_modelVersion_idx')
    .using('btree', table.modelVersion.asc()),

  // HNSW индекс для быстрого ANN поиска создаётся через SQL миграцию
]);

/**
 * Метаданные для UserInterestVector
 * Note: productsPurchased и reviewsWritten удалены, так как Product больше не существует
 */
export interface UserInterestMetadata {
  coursesCompleted: number;
  coursesInWishlist: number;
  coursesPurchased: number;
  postsLiked: number;
  /** Количество поведенческих сигналов из UserAction (добавлено в v2) */
  behavioralSignalsCount?: number;
  lastActions: Array<{
    type: string;
    entityType: string;
    entityId: number;
    weight: number;
  }>;
}

/**
 * UserInterestVector - агрегированный вектор интересов пользователя
 *
 * Вычисляется на основе:
 * - CourseProgress (завершённые курсы)
 * - CourseWishlist (избранное)
 * - Order (покупки курсов)
 * - Like (лайки постов)
 */
export const userInterestVector = pgTable('UserInterestVector', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  userId: integer().notNull(),

  // Агрегированный вектор интересов
  interestVector: vector({ dimensions: 384 }).notNull(),

  // Метаданные для отладки
  metadata: jsonb().$type<UserInterestMetadata>(),

  // Версия алгоритма агрегации
  algorithmVersion: text().default('v1').notNull(),

  // Когда последний раз пересчитывался
  computedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  // Один вектор на пользователя
  uniqueIndex('UserInterestVector_userId_key')
    .using('btree', table.userId.asc()),

  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserInterestVector_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),

  // HNSW индекс для поиска похожих пользователей создаётся через SQL миграцию
]);
